import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { IntakeDomain, CustomFieldDefinition } from '../../orders/api/intakeApi'
import {
  fieldConfigApi,
  type MerchantFieldConfigResponse,
  type SolutionCatalogResponse,
} from '../api/fieldConfigApi'

const MERCHANT_KEY = 'field-config-merchant'
const CATALOG_KEY = 'field-config-catalog'

function merchantQueryKey(merchantId: string, domain: IntakeDomain) {
  return [MERCHANT_KEY, merchantId, domain] as const
}

function catalogQueryKey(solutionId: string, domain: IntakeDomain) {
  return [CATALOG_KEY, solutionId, domain] as const
}

// ── Merchant config ────────────────────────────────────────────────────────────

/** Fetch the merchant config (template + merchant fields + effective) for one domain. */
export function useMerchantFieldConfig(merchantId: string, domain: IntakeDomain) {
  return useQuery<MerchantFieldConfigResponse>({
    queryKey: merchantQueryKey(merchantId, domain),
    queryFn: () => fieldConfigApi.getMerchantConfig(merchantId, domain),
    enabled: !!merchantId,
  })
}

/** Upsert the merchant's own field list. */
export function useSaveMerchantFieldConfig(merchantId: string, domain: IntakeDomain) {
  const qc = useQueryClient()
  const key = merchantQueryKey(merchantId, domain)

  return useMutation({
    mutationFn: (fields: CustomFieldDefinition[]) => fieldConfigApi.saveMerchantConfig(merchantId, domain, fields),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key })
    },
  })
}

/** Reset (DELETE) the merchant config — the solution template applies verbatim again. */
export function useResetMerchantFieldConfig(merchantId: string, domain: IntakeDomain) {
  const qc = useQueryClient()
  const key = merchantQueryKey(merchantId, domain)

  return useMutation({
    mutationFn: () => fieldConfigApi.resetMerchantConfig(merchantId, domain),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key })
    },
  })
}

// ── Solution template (TCS-admin) ────────────────────────────────────────────────

/** Fetch the solution template for one domain. */
export function useSolutionCatalog(solutionId: string, domain: IntakeDomain) {
  return useQuery<SolutionCatalogResponse>({
    queryKey: catalogQueryKey(solutionId, domain),
    queryFn: () => fieldConfigApi.getSolutionCatalog(solutionId, domain),
    enabled: !!solutionId,
  })
}

/** Upsert the solution template for one domain. Invalidates dependent merchant configs too. */
export function useSaveSolutionCatalog(solutionId: string, domain: IntakeDomain) {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (fields: CustomFieldDefinition[]) =>
      fieldConfigApi.saveSolutionCatalog(solutionId, domain, fields),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: catalogQueryKey(solutionId, domain) })
      // Unconfigured merchants inherit the template — their effective schema changes with it.
      qc.invalidateQueries({ queryKey: [MERCHANT_KEY] })
    },
  })
}
